import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  ArrowLeft, TrendingUp, TrendingDown, Minus, Globe, Newspaper,
  ExternalLink, RefreshCw, Calendar, Info, Users, BookOpen, ChevronRight,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { formatDate } from '../utils/formatters';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const NEWS_CATEGORIES = ['ALL', 'Markets', 'RBI/Policy', 'FII/DII', 'Global Cues', 'Earnings', 'Geopolitical'];

const INDEX_META = {
  '^DJI':     { color: '#3b82f6', comment: 'US blue chips — sets overnight sentiment. Big moves here = Nifty gap open.' },
  '^GSPC':    { color: '#3b82f6', comment: 'Broad US market — FII flows track this closely. Watch direction, not just level.' },
  '^IXIC':    { color: '#3b82f6', comment: 'Tech-heavy. Green NASDAQ = bullish mood for IT stocks (TCS, Infy, Wipro).' },
  '^N225':    { color: '#a78bfa', comment: 'Japan. Yen weakness = risk-on Asia. Strong Nikkei often drags Nifty higher.' },
  '^HSI':     { color: '#a78bfa', comment: 'China proxy. Impacts metals and commodities — watch for TATASTEEL, Hindalco.' },
  '^FTSE':    { color: '#6ee7b7', comment: 'UK — mining and energy heavy. Tracks crude oil direction.' },
  '^GDAXI':   { color: '#6ee7b7', comment: 'European industrial bellwether. Relevant for mid-session cues.' },
  'DX-Y.NYB': { color: '#f59e0b', comment: 'Stronger Dollar = FII selling in India. Most critical macro signal to watch.' },
  'CL=F':     { color: '#f59e0b', comment: 'Crude up = inflation pressure, OMCs suffer (BPCL, IOC). Crude down = relief rally.' },
  'GC=F':     { color: '#f59e0b', comment: 'Gold rises on fear. High gold = cautious market. Watch with DXY for direction.' },
};

function formatIST(isoStr) {
  if (!isoStr) return '—';
  try {
    return new Date(isoStr).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit', month: 'short',
      hour: '2-digit', minute: '2-digit',
      hour12: false,
    }) + ' IST';
  } catch { return '—'; }
}

function sentimentColor(label, colors) {
  if (label === 'positive') return colors.profit;
  if (label === 'negative') return colors.loss;
  return colors.secondaryText;
}

function IndexCard({ idx, colors, isDark }) {
  const meta = INDEX_META[idx.symbol] || { color: colors.accent, comment: '' };
  const up   = idx.change_pct > 0;
  const down = idx.change_pct < 0;
  const chgColor = up ? colors.profit : down ? colors.loss : colors.secondaryText;
  const Icon = up ? TrendingUp : down ? TrendingDown : Minus;

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', borderRadius: 14, overflow: 'hidden',
      background: `linear-gradient(135deg, ${meta.color}16 0%, ${meta.color}06 100%)`,
      border: `1px solid ${meta.color}28`,
      borderBottom: `2px solid ${meta.color}55`,
    }}>
      <div style={{ padding: '8px 10px 6px', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Name + icon row */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 4 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: colors.primaryText, margin: 0, lineHeight: 1.3 }}>{idx.name}</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 1 }}>
              <div style={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: meta.color, flexShrink: 0 }} />
              <p style={{ fontSize: 9, color: colors.secondaryText, margin: 0 }}>{idx.region}</p>
            </div>
          </div>
          <div style={{ width: 22, height: 22, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, backgroundColor: `${chgColor}18` }}>
            <Icon style={{ width: 11, height: 11, color: chgColor }} />
          </div>
        </div>
        {/* Price */}
        <p style={{ fontSize: 14, fontWeight: 800, color: colors.primaryText, margin: 0, fontVariantNumeric: 'tabular-nums' }}>
          {idx.close != null ? idx.close.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}
        </p>
        {/* Change */}
        {idx.change_pct != null && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: chgColor, fontVariantNumeric: 'tabular-nums' }}>
              {idx.change_pct >= 0 ? '+' : ''}{idx.change_pct.toFixed(2)}%
            </span>
            {idx.change_abs != null && (
              <span style={{ fontSize: 9, color: `${chgColor}90`, fontVariantNumeric: 'tabular-nums' }}>
                ({idx.change_abs >= 0 ? '+' : ''}{idx.change_abs.toFixed(2)})
              </span>
            )}
          </div>
        )}
      </div>
      <div style={{ height: 1, backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : '#f3f4f6' }} />
      <div style={{ padding: '5px 10px 7px', display: 'flex', flexDirection: 'column', gap: 3 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Calendar style={{ width: 9, height: 9, flexShrink: 0, color: colors.secondaryText }} />
          <span style={{ fontSize: 9, color: colors.secondaryText }}>
            {idx.price_type === 'open' ? 'Opening' : 'Close'}: {idx.price_date ? formatDate(idx.price_date) : '—'}
          </span>
        </div>
        <p style={{ fontSize: 9, lineHeight: 1.4, color: `${colors.secondaryText}99`, margin: 0, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{meta.comment}</p>
      </div>
    </div>
  );
}


function MoodCard({ mood, simDate, dataDate, colors, isDark }) {
  if (!mood) return null;
  return (
    <div className="rounded-2xl overflow-hidden"
      style={{
        background: `linear-gradient(135deg, ${mood.color}18 0%, ${mood.color}06 100%)`,
        border: `1px solid ${mood.color}30`,
        borderBottom: `2px solid ${mood.color}70`,
      }}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-4 py-3"
        style={{ backgroundColor: `${mood.color}0d`, borderBottom: `1px solid ${mood.color}20` }}>
        <div className="flex items-center gap-2">
          <Globe className="w-4 h-4" style={{ color: mood.color }} />
          <span className="text-[12px] font-bold tracking-wider uppercase" style={{ color: colors.secondaryText }}>
            Market Mood
          </span>
        </div>
        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full self-start sm:self-auto"
          style={{ backgroundColor: `${colors.accent}12`, border: `1px solid ${colors.accent}22` }}>
          <Info className="w-3 h-3 flex-shrink-0" style={{ color: colors.secondaryText }} />
          <span className="text-[10px]" style={{ color: colors.secondaryText }}>
            US/EU: {dataDate ? formatDate(dataDate) : '—'} close &middot; Asia/Macro: {simDate ? formatDate(simDate) : '—'} open
          </span>
        </div>
      </div>
      <div className="px-4 py-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="w-2.5 h-2.5 rounded-full animate-pulse" style={{ backgroundColor: mood.color }} />
          <span className="text-[18px] font-bold" style={{ color: mood.color }}>{mood.label}</span>
        </div>
        <p className="text-[13px] leading-relaxed" style={{ color: colors.secondaryText }}>{mood.summary}</p>
      </div>
    </div>
  );
}

function NewsItem({ item, colors, isDark }) {
  const sc = sentimentColor(item.sentiment_label, colors);
  const hasUrl = typeof item.url === 'string' && item.url.startsWith('http');

  const handleClick = () => {
    if (!hasUrl) return;
    window.open(item.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      onClick={handleClick}
      className="flex items-start gap-3 px-3 py-3 rounded-xl transition-colors"
      style={{
        background: 'linear-gradient(135deg, rgba(139,92,246,0.08) 0%, rgba(99,102,241,0.03) 100%)',
        border: '1px solid rgba(139,92,246,0.18)',
        cursor: hasUrl ? 'pointer' : 'default',
      }}
      onMouseEnter={e => { if (hasUrl) e.currentTarget.style.borderColor = 'rgba(139,92,246,0.35)'; }}
      onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(139,92,246,0.18)'}
    >
      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5" style={{ backgroundColor: sc }} />
      <div className="flex-1 min-w-0">
        <p className="text-[12px] leading-snug font-medium" style={{ color: colors.primaryText }}>
          {item.title}
        </p>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          {item.source && (
            <span className="text-[10px]" style={{ color: colors.secondaryText }}>{item.source}</span>
          )}
          {item.source && item.published_at && (
            <span className="text-[10px]" style={{ color: `${colors.secondaryText}60` }}>&middot;</span>
          )}
          {item.published_at && (
            <span className="text-[10px] tabular-nums" style={{ color: colors.secondaryText }}>
              {formatIST(item.published_at)}
            </span>
          )}
          {item.category && item.category !== 'ALL' && (
            <>
              <span className="text-[10px]" style={{ color: `${colors.secondaryText}60` }}>&middot;</span>
              <span className="text-[9px] px-1.5 py-0.5 rounded-full font-semibold"
                style={{ backgroundColor: `${colors.accent}18`, color: colors.accent }}>
                {item.category}
              </span>
            </>
          )}
        </div>
      </div>
      {hasUrl && (
        <ExternalLink className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: `${colors.secondaryText}60` }} />
      )}
    </div>
  );
}

function fmt(n) {
  if (n == null) return '—';
  return Math.abs(n).toLocaleString('en-IN');
}

function FiiDiiWidget({ fiiDii, colors, isDark }) {
  const [showGuide, setShowGuide] = useState(false);
  const { fii, dii, date: flowDate } = fiiDii || {};

  if (!fiiDii) {
    return (
      <div className="rounded-2xl overflow-hidden"
        style={{ background: isDark ? 'rgba(255,255,255,0.03)' : '#fafafa', border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e5e7eb'}` }}>
        <div className="flex items-center justify-between px-4 py-2.5"
          style={{ background: isDark ? 'rgba(255,255,255,0.04)' : '#f3f4f6', borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#e5e7eb'}` }}>
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded-lg flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}>
              <Users className="w-3 h-3 text-white" />
            </div>
            <span className="text-[11px] font-black uppercase tracking-widest" style={{ color: colors.secondaryText }}>
              FII / DII Activity
            </span>
          </div>
        </div>
        <div className="px-4 py-4 text-center">
          <p className="text-[12px]" style={{ color: colors.secondaryText }}>
            FII/DII data fetches daily at 6:30 PM IST. Check back after the next run.
          </p>
        </div>
      </div>
    );
  }

  const fiiNet    = fii?.net ?? 0;
  const diiNet    = dii?.net ?? 0;
  const fiiUp     = fiiNet >= 0;
  const diiUp     = diiNet >= 0;
  const fiiColor  = fiiUp ? '#16C784' : '#EF4444';
  const diiColor  = diiUp ? '#16C784' : '#EF4444';
  const FiiIcon   = fiiUp ? TrendingUp : TrendingDown;
  const DiiIcon   = diiUp ? TrendingUp : TrendingDown;

  return (
    <>
    <div className="rounded-2xl overflow-hidden"
      style={{ background: isDark ? 'rgba(255,255,255,0.03)' : '#fafafa', border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e5e7eb'}` }}>

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5"
        style={{ background: isDark ? 'rgba(255,255,255,0.04)' : '#f3f4f6', borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#e5e7eb'}` }}>
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 rounded-lg flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}>
            <Users className="w-3 h-3 text-white" />
          </div>
          <span className="text-[11px] font-black uppercase tracking-widest" style={{ color: colors.secondaryText }}>
            FII / DII Activity
          </span>
        </div>
        <div className="flex items-center gap-2">
          {flowDate && (
            <div className="flex items-center gap-1">
              <Calendar className="w-3 h-3" style={{ color: colors.secondaryText }} />
              <span className="text-[10px]" style={{ color: colors.secondaryText }}>
                as of {formatDate(flowDate)}
              </span>
            </div>
          )}
          <button onClick={() => setShowGuide(v => !v)}
            className="w-5 h-5 rounded-full flex items-center justify-center transition-all"
            style={{ background: showGuide ? '#6366f130' : 'transparent', border: `1px solid ${showGuide ? '#6366f1' : colors.border}` }}>
            <Info className="w-3 h-3" style={{ color: showGuide ? '#6366f1' : colors.secondaryText }} />
          </button>
        </div>
      </div>

      {/* Two-column body */}
      <div className="grid grid-cols-2 divide-x" style={{ borderColor: isDark ? 'rgba(255,255,255,0.06)' : '#e5e7eb' }}>
        {/* FII */}
        <div className="px-4 py-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: colors.secondaryText }}>FII</span>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
              style={{ background: `${fiiColor}18`, color: fiiColor }}>
              {fiiUp ? 'NET LONG' : 'NET SHORT'}
            </span>
          </div>
          <div className="flex items-end gap-2">
            <FiiIcon className="w-5 h-5 flex-shrink-0" style={{ color: fiiColor }} />
            <span className="text-[18px] font-black tabular-nums leading-none" style={{ color: fiiColor }}>
              {fiiNet >= 0 ? '+' : '-'}{fmt(fiiNet)}
            </span>
          </div>
          <div className="space-y-1 pt-1" style={{ borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : '#f3f4f6'}` }}>
            <div className="flex justify-between text-[10px]">
              <span style={{ color: colors.secondaryText }}>Long</span>
              <span className="tabular-nums font-semibold" style={{ color: '#16C784' }}>{fmt(fii?.total_long)}</span>
            </div>
            <div className="flex justify-between text-[10px]">
              <span style={{ color: colors.secondaryText }}>Short</span>
              <span className="tabular-nums font-semibold" style={{ color: '#EF4444' }}>{fmt(fii?.total_short)}</span>
            </div>
            <div className="flex justify-between text-[10px]">
              <span style={{ color: colors.secondaryText }}>Idx Fut</span>
              <span className="tabular-nums" style={{ color: colors.secondaryText }}>
                {(fii?.fut_idx_long ?? 0) >= (fii?.fut_idx_short ?? 0) ? '+' : '-'}
                {fmt((fii?.fut_idx_long ?? 0) - (fii?.fut_idx_short ?? 0))}
              </span>
            </div>
          </div>
        </div>

        {/* DII */}
        <div className="px-4 py-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: colors.secondaryText }}>DII</span>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
              style={{ background: `${diiColor}18`, color: diiColor }}>
              {diiUp ? 'NET LONG' : 'NET SHORT'}
            </span>
          </div>
          <div className="flex items-end gap-2">
            <DiiIcon className="w-5 h-5 flex-shrink-0" style={{ color: diiColor }} />
            <span className="text-[18px] font-black tabular-nums leading-none" style={{ color: diiColor }}>
              {diiNet >= 0 ? '+' : '-'}{fmt(diiNet)}
            </span>
          </div>
          <div className="space-y-1 pt-1" style={{ borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : '#f3f4f6'}` }}>
            <div className="flex justify-between text-[10px]">
              <span style={{ color: colors.secondaryText }}>Long</span>
              <span className="tabular-nums font-semibold" style={{ color: '#16C784' }}>{fmt(dii?.total_long)}</span>
            </div>
            <div className="flex justify-between text-[10px]">
              <span style={{ color: colors.secondaryText }}>Short</span>
              <span className="tabular-nums font-semibold" style={{ color: '#EF4444' }}>{fmt(dii?.total_short)}</span>
            </div>
            <div className="flex justify-between text-[10px]">
              <span style={{ color: colors.secondaryText }}>Idx Fut</span>
              <span className="tabular-nums" style={{ color: colors.secondaryText }}>
                {(dii?.fut_idx_long ?? 0) >= (dii?.fut_idx_short ?? 0) ? '+' : '-'}
                {fmt((dii?.fut_idx_long ?? 0) - (dii?.fut_idx_short ?? 0))}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Footer note */}
      <div className="px-4 py-2" style={{ background: isDark ? 'rgba(255,255,255,0.02)' : '#f9fafb', borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : '#e5e7eb'}` }}>
        <p className="text-[10px]" style={{ color: `${colors.secondaryText}90` }}>
          F&O participant contracts · Positive net = institutional long bias · Source: NSE
        </p>
      </div>
    </div>

    {/* How to read — modal overlay */}
    {showGuide && (
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
        onClick={() => setShowGuide(false)}>
        <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.5)' }} />
        <div className="relative w-full max-w-sm rounded-2xl p-5 space-y-3 text-[12px] leading-relaxed"
          style={{ background: colors.cardBg, border: `1px solid ${isDark ? 'rgba(99,102,241,0.3)' : '#c7d2fe'}` }}
          onClick={e => e.stopPropagation()}>
          <div className="flex items-center justify-between">
            <p className="text-[13px] font-bold" style={{ color: '#6366f1' }}>How to read FII / DII</p>
            <button onClick={() => setShowGuide(false)}
              className="text-[18px] leading-none"
              style={{ color: colors.secondaryText }}>×</button>
          </div>
          <p style={{ color: colors.secondaryText }}>
            <span className="font-semibold" style={{ color: colors.primaryText }}>Net = Long − Short.</span> Positive = more longs (bullish). Negative = more shorts (hedged or bearish).
          </p>
          <p style={{ color: colors.secondaryText }}>
            <span className="font-semibold" style={{ color: colors.primaryText }}>FII</span> are foreign institutions — their positioning drives large moves. Watch if FII net shifts from negative → positive day over day.
          </p>
          <p style={{ color: colors.secondaryText }}>
            <span className="font-semibold" style={{ color: colors.primaryText }}>DII</span> (mutual funds, insurance) are almost always net short in F&O — they hedge their equity holdings, not a bearish bet.
          </p>
          <p style={{ color: colors.secondaryText }}>
            <span className="font-semibold" style={{ color: colors.primaryText }}>Idx Fut</span> = FII net in Nifty/BankNifty futures. Large negative = active hedge, expect pressure.
          </p>
          <p className="text-[11px] font-semibold px-3 py-2 rounded-xl" style={{ background: '#f59e0b18', color: '#f59e0b' }}>
            Key signal: FII net flipping from negative → positive = institutions buying. Trade with that move.
          </p>
        </div>
      </div>
    )}
    </>
  );
}


export default function GlobalCuesPage() {
  const { colors, theme } = useTheme();
  const { token } = useAuth();
  const navigate = useNavigate();
  const isDark = theme === 'dark';

  const [activeTab, setActiveTab]         = useState('mood');
  const [newsCategory, setNewsCategory]   = useState('ALL');

  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const [news, setNews]                 = useState([]);
  const [newsLoading, setNewsLoading]   = useState(false);
  const [newsDisplayDate, setNewsDisplayDate] = useState(null);
  const [newsIsFallback, setNewsIsFallback]   = useState(false);

  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

  const loadMood = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    setError(null);
    try {
      const res = await axios.get(`${API_URL}/global-cues`, { headers: authHeader });
      setData(res.data);
    } catch {
      setError('Failed to load global cues. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []); // eslint-disable-line

  const loadNews = useCallback(async (category) => {
    setNewsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/global-cues/news`, {
        params: { category },
        headers: authHeader,
      });
      setNews(res.data.news || []);
      setNewsDisplayDate(res.data.display_date || null);
      setNewsIsFallback(res.data.is_fallback || false);
    } catch {
      setNews([]);
    } finally {
      setNewsLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => { loadMood(); }, [loadMood]);

  useEffect(() => {
    if (activeTab === 'news') loadNews(newsCategory);
  }, [activeTab, newsCategory, loadNews]);

  const handleRefresh = () => {
    if (activeTab === 'mood') loadMood(true);
    else loadNews(newsCategory);
  };

  if (loading) {
    return (
      <div className="space-y-3 animate-pulse max-w-2xl mx-auto">
        <div className="h-10 rounded-xl" style={{ backgroundColor: colors.skeletonBgAlt }} />
        <div className="h-28 rounded-2xl" style={{ backgroundColor: colors.skeletonBgAlt }} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-40 rounded-2xl" style={{ backgroundColor: colors.skeletonBgAlt }} />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3">
        <Globe className="w-8 h-8" style={{ color: colors.secondaryText }} />
        <p className="text-[13px]" style={{ color: colors.secondaryText }}>{error}</p>
        <button onClick={() => loadMood()} className="text-[12px] px-3 py-1.5 rounded-lg font-semibold"
          style={{ backgroundColor: `${colors.accent}20`, color: colors.accent }}>
          Retry
        </button>
      </div>
    );
  }

  const sortedIndices = data?.indices || [];
  const hasIndices = data?.indices_data_ready;

  return (
    <div className="space-y-3 pb-6 max-w-2xl mx-auto">

      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <button onClick={() => navigate(-1)} style={{ padding: 6, borderRadius: 8, backgroundColor: colors.cardBg, border: `1px solid ${colors.border}`, display: 'flex', cursor: 'pointer' }}>
          <ArrowLeft style={{ width: 16, height: 16, color: colors.primaryText }} />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={{ fontSize: 16, fontWeight: 800, color: colors.primaryText, margin: 0 }}>Global Cues</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
            <Calendar style={{ width: 10, height: 10, color: colors.secondaryText }} />
            <span style={{ fontSize: 11, color: colors.secondaryText }}>
              Sim day: {data?.sim_date ? formatDate(data.sim_date) : '—'}
            </span>
          </div>
        </div>
        <button onClick={handleRefresh} disabled={refreshing || newsLoading}
          style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 10, fontSize: 11, fontWeight: 700, cursor: 'pointer', backgroundColor: `${colors.accent}15`, color: colors.accent, border: `1px solid ${colors.accent}30`, flexShrink: 0 }}>
          <RefreshCw style={{ width: 11, height: 11, ...(refreshing || newsLoading ? { animation: 'spin 1s linear infinite' } : {}) }} />
          Refresh
        </button>
      </div>

      {/* Learning nudge */}
      <button
        onClick={() => navigate('/learning?view=level&level=14&card=0')}
        className="w-full flex items-center justify-between px-3 py-2 rounded-xl text-left"
        style={{ background: 'linear-gradient(135deg, #6c63ff12 0%, #6c63ff04 100%)', border: '1px solid #6c63ff28' }}>
        <div className="flex items-center gap-2">
          <BookOpen className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#6c63ff' }} />
          <span className="text-[11px] font-medium" style={{ color: 'rgba(255,255,255,0.55)' }}>
            Want context? <span style={{ color: '#6c63ff' }}>Learn Global Cues & Market Context</span>
          </span>
        </div>
        <ChevronRight className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#6c63ff50' }} />
      </button>

      {/* Tab switcher */}
      <div className="rounded-xl p-1.5 grid grid-cols-2 gap-1.5"
        style={{ backgroundColor: colors.inputBg, border: `1px solid ${colors.border}` }}>
        {[
          { key: 'mood', label: 'Market Mood', Icon: Globe },
          { key: 'news', label: 'News',        Icon: Newspaper },
        ].map(({ key, label, Icon }) => {
          const active = activeTab === key;
          return (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className="flex items-center justify-center gap-1.5 py-2 rounded-lg text-[13px] font-semibold transition-all"
              style={{
                backgroundColor: active ? '#8b5cf6' : 'transparent',
                color: active ? '#fff' : colors.secondaryText,
                border: `1px solid ${active ? '#8b5cf6' : colors.border}`,
              }}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          );
        })}
      </div>

      {/* ── MARKET MOOD TAB ── */}
      {activeTab === 'mood' && (
        <>
          {/* 1. Market mood */}
          {hasIndices
            ? <MoodCard mood={data.mood} simDate={data.sim_date} dataDate={data.data_date} colors={colors} isDark={isDark} />
            : (
              <div className="rounded-xl px-4 py-3"
                style={{ background: `linear-gradient(135deg, #8b5cf614 0%, #8b5cf604 100%)`, border: '1px solid #8b5cf628' }}>
                <p className="text-[12px]" style={{ color: colors.secondaryText }}>
                  Global indices snapshot runs daily at 8:45 AM IST. Check back after the next fetch.
                </p>
              </div>
            )
          }

          {/* 2. FII / DII */}
          <FiiDiiWidget fiiDii={data?.fii_dii} colors={colors} isDark={isDark} />

          {/* 3. Global index cards — always 2 columns */}
          {hasIndices && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              {sortedIndices.map(idx => (
                <IndexCard key={idx.symbol} idx={idx} colors={colors} isDark={isDark} />
              ))}
            </div>
          )}
          {!hasIndices && (
            <div className="rounded-2xl p-6 text-center"
              style={{ background: 'linear-gradient(135deg, #06b6d414 0%, #8b5cf606 100%)', border: '1px solid #06b6d428' }}>
              <TrendingUp className="w-8 h-8 mx-auto mb-2" style={{ color: '#06b6d4' }} />
              <p className="text-[13px] font-semibold mb-1" style={{ color: colors.primaryText }}>Building snapshot...</p>
              <p className="text-[12px]" style={{ color: colors.secondaryText }}>Data is fetched daily at 8:45 AM IST.</p>
            </div>
          )}
        </>
      )}

      {/* ── NEWS TAB ── */}
      {activeTab === 'news' && (
        <div className="space-y-3">

          {/* Category pills */}
          <div className="flex gap-1.5 flex-wrap">
            {NEWS_CATEGORIES.map(cat => {
              const active = newsCategory === cat;
              return (
                <button
                  key={cat}
                  onClick={() => setNewsCategory(cat)}
                  className="px-2.5 py-1 rounded-full text-[11px] font-semibold transition-all"
                  style={{
                    backgroundColor: active ? '#8b5cf6' : `${colors.inputBg}`,
                    color: active ? '#fff' : colors.secondaryText,
                    border: `1px solid ${active ? '#8b5cf6' : colors.border}`,
                  }}
                >
                  {cat}
                </button>
              );
            })}
          </div>

          {/* News list */}
          {newsLoading ? (
            <div className="space-y-2 animate-pulse">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-16 rounded-xl" style={{ backgroundColor: colors.skeletonBgAlt }} />
              ))}
            </div>
          ) : news.length === 0 ? (
            <div className="rounded-2xl p-6 text-center"
              style={{ background: 'linear-gradient(135deg, #a78bfa14 0%, #a78bfa04 100%)', border: '1px solid #a78bfa28' }}>
              <Newspaper className="w-8 h-8 mx-auto mb-2" style={{ color: '#a78bfa' }} />
              <p className="text-[13px] font-semibold mb-1" style={{ color: colors.primaryText }}>No news yet</p>
              <p className="text-[12px]" style={{ color: colors.secondaryText }}>
                News fetches daily at 8:00 AM IST. Check back after the next run.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-[10px] px-1" style={{ color: colors.secondaryText }}>
                {news.length} article{news.length !== 1 ? 's' : ''} &middot;{' '}
                {newsIsFallback
                  ? <>latest available &middot; <span style={{ color: '#f59e0b' }}>{newsDisplayDate ? formatDate(newsDisplayDate) : '—'}</span> <span style={{ color: `${colors.secondaryText}70` }}>(sim date has no data yet)</span></>
                  : <>pre-market window for {newsDisplayDate ? formatDate(newsDisplayDate) : '—'}</>
                }
              </p>
              {news.map((item, i) => (
                <NewsItem key={i} item={item} colors={colors} isDark={isDark} />
              ))}
            </div>
          )}

        </div>
      )}

    </div>
  );
}
